import React, { useState, useEffect } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { Grid } from '@mui/material';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import EditIcon from '@mui/icons-material/Edit';
import FileCopyRoundedIcon from '@mui/icons-material/FileCopyRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import PersonIcon from '@mui/icons-material/Person';
import MapsHomeWorkIcon from '@mui/icons-material/MapsHomeWork';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import PreloaderComponent from '#component/preloader';
import {
  changeItemsPerPage,
  changeOrderAndOrderBy,
  fetchContacts
} from '#action/addressbook/addressbook-contacts-paginated';
import { getAddressString } from '#component/addressbook/helper';
import NotFoundMessage from '#component/general/not-found-message';
import MobileTabs from '#component/general/mobile-tabs/mobile-tabs';
import MobileDoubleButtons from '#component/general/mobile-double-buttons/mobile-double-buttons';
import MobileDrawerMenu from '#component/general/mobile-drawer-menu';
import DeleteAddressbookDialog from '#component/addressbook/dashboard/delete-addressbook-dialog';
import DeleteContactDialog from '#component/addressbook/dashboard/delete-contact-dialog';
import { deleteAddressbook } from '#action/addressbook/addressbook';
import { deleteAddressbookContact } from '#action/addressbook/addressbook-contact';
import { SUCCESS } from '#action/types';
import SimpleCardTemplate from '#component/general/simple-card-template';
import { StyledGrid, CreateButton, MobileSection } from './styles';
import { writeToClipboard } from '#helper';

const DEFAULT_ITEMS_PER_PAGE = 20;
const DEFAULT_ORDER = 'asc';
const DEFAULT_ORDER_BY = 'addressbook';

const getDialogContentText = contact => {
  let text = 'Do you want to delete the "';

  if (contact.firstName || contact.lastName) {
    text += `${contact?.firstName ?? ''} ${contact?.lastName ?? ''}`;
  } else if (contact.companyName) {
    text += contact.companyName;
  }
  text += '"?';
  return text;
};

const MobileView = ({ setMobileChosenTab }) => {
  const dispatch = useDispatch();
  const {
    contacts,
    status,
    order,
    orderBy,
    itemsPerPage,
    addressbooks
  } = useSelector(s => s.addressbookContactsPaginated, shallowEqual);
  const {
    addressbook: addressbooksFilter,
    pristine,
    initial
  } = useSelector(s => s.listFilter, shallowEqual);
  const filter = addressbooksFilter?.search?.value;
  const filterPristine = pristine && initial?.addressbook?.search?.value === filter;
  const history = useHistory();
  const [isContactDrawerMenuOpen, setIsContactDrawerMenuOpen] = useState(false);
  const [isAddressBookMenuOpen, setIsAddressBookMenuOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [selectedAddressBook, setSelectedAddressBook] = useState(null);
  const [openDeleteAddressBookDialog, setOpenDeleteAddressBookDialog] = useState(false);
  const [openDeleteContactDialog, setOpenDeleteContactDialog] = useState(false);
  const loading = status === 'loading';
  const isEmptyContacts = status === 'succeeded' && !contacts.length && !filter;
  const emptyData = addressbooks?.filter(ab => ab.contactsCount === 0) || [];

  const handleContactMenuOpen = () => {
    setIsContactDrawerMenuOpen(true);
  };

  const handleContactMenuClose = () => {
    setIsContactDrawerMenuOpen(false);
    setSelectedContact(null);
  };

  const handleAddressMenuOpen = () => {
    setIsAddressBookMenuOpen(true);
  };

  const handleAddressMenuClose = () => {
    setIsAddressBookMenuOpen(false);
    setSelectedAddressBook(null);
  };

  useEffect(() => {
    if (itemsPerPage !== DEFAULT_ITEMS_PER_PAGE) {
      dispatch(changeItemsPerPage(DEFAULT_ITEMS_PER_PAGE));
    }
    if (order !== DEFAULT_ORDER || orderBy !== DEFAULT_ORDER_BY) {
      dispatch(changeOrderAndOrderBy(DEFAULT_ORDER, DEFAULT_ORDER_BY));
    }
  }, [dispatch, itemsPerPage, order, orderBy]);

  if (!contacts.length && !addressbooks?.length && loading) {
    return <PreloaderComponent />;
  }

  const handleEditContactClick = () => history.push(`/addressbook/contact/edit/${selectedContact.id}`);

  const handleCopyAddressClick = () => {
    const addressString = getAddressString(selectedContact);
    writeToClipboard(addressString);
    dispatch({
      type: SUCCESS,
      payload: {
        message: 'Address copied to clipboard'
      }
    });
    handleContactMenuClose();
  };

  const contactMenuItems = [
    { name: 'Edit contact', onClick: handleEditContactClick, icon: <EditIcon /> },
    { name: 'Copy address', onClick: handleCopyAddressClick, icon: <FileCopyRoundedIcon /> },
    { name: 'Delete contact', onClick: () => setOpenDeleteContactDialog(true), icon: <DeleteRoundedIcon /> }
  ];

  const handleEditAddressbookClick = () => history.push(`/addressbook/edit/${selectedAddressBook?.id}`);
  const handleEditAddContactClick = () => history.push(`/addressbook/contact/create${selectedAddressBook.id ? `?addressbookId=${selectedAddressBook.id}` : ''}`);

  const handleDeleteAddressBookClick = () => {
    setOpenDeleteAddressBookDialog(true);
  };

  const handleDeleteAddressBookClose = () => {
    setOpenDeleteAddressBookDialog(false);
    setIsAddressBookMenuOpen(false);
  };

  const handleDeleteAddressBook = async () => {
    const result = await dispatch(deleteAddressbook(selectedAddressBook?.id));
    if (result?.success) {
      handleDeleteAddressBookClose();
      dispatch(fetchContacts({ refetchAddressbooks: true }));
    }
  };

  const handleDeleteContactClose = () => {
    setOpenDeleteContactDialog(false);
    setIsContactDrawerMenuOpen(false);
  };

  const handleDeleteContact = async () => {
    const result = await dispatch(deleteAddressbookContact(selectedContact?.id));
    if (result?.success) {
      handleDeleteContactClose();
      dispatch(fetchContacts({ refetchAddressbooks: true }));
    }
  };

  const addressBookMenuItems = [
    { name: 'Add contact', onClick: handleEditAddContactClick, icon: <PersonAddAlt1Icon /> },
    { name: 'Edit address book', onClick: handleEditAddressbookClick, icon: <EditIcon /> },
    { name: 'Delete address book', onClick: handleDeleteAddressBookClick, icon: <DeleteRoundedIcon /> }
  ];

  const tabContents = [
    <div>
      {!contacts.length && filter?.length && (
        <NotFoundMessage>Contacts not found</NotFoundMessage>
      )}
      {isEmptyContacts && filterPristine ? (
        <NotFoundMessage>
          <CreateButton
            variant="contained"
            color="secondary"
            aria-label="Create contact"
            component={Link}
            to="/addressbook/contact/create"
          >
            CREATE CONTACT
          </CreateButton>
        </NotFoundMessage>
      ) : (
        <StyledGrid container columnSpacing={1} mt={1} mb={8} isLoading={loading}>
          {contacts.map(contact => (
            <Grid item xs={12} sm={6} md={6} key={contact.id}>
              <SimpleCardTemplate
                onCardClickHeader={() => history.push(`/addressbook/contact/edit/${contact.id}`)}
                title={`${contact?.firstName} ${contact?.lastName}`}
                icon={<PersonIcon color="secondary" />}
                cardContents={[
                  {
                    title: 'Company name',
                    content: `${contact.companyName ? `${contact.companyName}` : ''}`
                  },
                  { title: 'Address book', content: contact.addressbook?.label },
                  { title: 'Address', content: getAddressString(contact) }
                ]}
                onClickMore={() => {
                  setSelectedContact(contact);
                  handleContactMenuOpen();
                }}
              />
            </Grid>
          ))}
        </StyledGrid>
      )}
    </div>,
    <div>
      {!filter && (
        <Grid container columnSpacing={1} mt={1} mb={8}>
          {addressbooks?.map(addressbook => (
            <Grid item xs={12} sm={6} md={6} key={addressbook.id}>
              <SimpleCardTemplate
                onCardClickHeader={() => history.push(`/addressbook/edit/${addressbook.id}`)}
                title={addressbook.label}
                icon={<MapsHomeWorkIcon color="secondary" />}
                cardContents={[
                  {
                    title: 'Project',
                    content: `${addressbook.project?.title}`
                  }
                ]}
                onClickMore={() => {
                  setSelectedAddressBook(addressbook);
                  handleAddressMenuOpen();
                }}
              />
            </Grid>
          ))}
        </Grid>
      )}
    </div>
  ];

  if (emptyData.length > 0) {
    tabContents.push(
      <div>
        <Grid container columnSpacing={1} mt={1} mb={8}>
          {emptyData?.map(emptyItem => (
            <Grid item xs={12} sm={6} md={6} key={emptyItem.id}>
              <SimpleCardTemplate
                onCardClickHeader={() => history.push(`/addressbook/edit/${emptyItem.id}`)}
                title={emptyItem?.label}
                icon={<MapsHomeWorkIcon color="secondary" />}
                cardContents={[
                  {
                    title: 'Project',
                    content: `${emptyItem.project?.title}`
                  }
                ]}
                onClickMore={() => {
                  setSelectedAddressBook(emptyItem);
                  handleAddressMenuOpen();
                }}
              />
            </Grid>
          ))}
        </Grid>
      </div>
    );
  }

  const tabLabels = ['Contacts', 'Address books'];
  if (emptyData.length > 0) {
    tabLabels.push('Empty Address books');
  }

  return (
    <MobileSection component="section" isEmptyContacts={isEmptyContacts}>
      <MobileTabs
        labels={tabLabels}
        contents={tabContents}
        setMobileChosenTab={setMobileChosenTab}
      />
      <MobileDoubleButtons
        firstLabel="create contact"
        secondLabel="create address book"
        firstAddress="/addressbook/contact/create"
        secondAddress="/addressbook/create"
      />
      <MobileDrawerMenu
        isOpen={isContactDrawerMenuOpen}
        onClose={handleContactMenuClose}
        menuTitle={`${selectedContact?.firstName} ${selectedContact?.lastName}`}
        menuItems={contactMenuItems}
      />
      <MobileDrawerMenu
        isOpen={isAddressBookMenuOpen}
        onClose={handleAddressMenuClose}
        menuTitle={selectedAddressBook?.label}
        menuItems={addressBookMenuItems}
      />
      <DeleteAddressbookDialog
        open={openDeleteAddressBookDialog}
        dialogTitle={`Do you want to delete the "${selectedAddressBook?.label ?? ''}"?`}
        entityTitle={selectedAddressBook?.label ?? ''}
        dialogContentText="Type the address book label to confirm"
        textFieldLabel="Label"
        onClose={handleDeleteAddressBookClose}
        onDelete={handleDeleteAddressBook}
      />
      <DeleteContactDialog
        open={openDeleteContactDialog}
        dialogContentText={getDialogContentText(selectedContact || '')}
        onClose={handleDeleteContactClose}
        onDelete={handleDeleteContact}
      />
    </MobileSection>
  );
};

export default MobileView;
